/**
 * `returned_after_clickout` — did the user come back after we sent them to a partner?
 *
 * It's the closest thing to a click-quality signal we have until W1 postbacks exist. Someone who
 * bounces straight back in four seconds did not book; someone gone eleven minutes might have.
 *
 * The timestamp is written by trackEvent itself whenever an `affiliate_clicked` goes out, so no
 * affiliate call site has to remember anything, and a new affiliate surface is covered the day it
 * ships. sessionStorage rather than memory: the clickout usually opens a new tab, and this one can
 * be reloaded or restored while the user is away.
 */

const KEY = "affiliate_clickout_at";

/** Anything longer and "they came back" stops meaning anything. */
const WINDOW_MS = 30 * 60 * 1000;

export const recordClickout = () => {
	try {
		sessionStorage.setItem(KEY, String(Date.now()));
	} catch {
		// Private mode or a full quota — losing this is not worth an exception in a tracking call.
	}
};

/**
 * Returns how long the user was away if they're back inside the window, else undefined.
 * Clears as it reads, so one clickout produces at most one return.
 */
export const takeSecondsAway = (): number | undefined => {
	try {
		const raw = sessionStorage.getItem(KEY);
		if (!raw) return undefined;
		sessionStorage.removeItem(KEY);
		const elapsed = Date.now() - Number(raw);
		if (!Number.isFinite(elapsed) || elapsed < 0 || elapsed >= WINDOW_MS) return undefined;
		return Math.round(elapsed / 1000);
	} catch {
		return undefined;
	}
};
